import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCamera, FiX, FiHome, FiLogOut, FiImage } from 'react-icons/fi';
import { auth, db, storage } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, addDoc, query, where, getDocs, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import AnimatedHero from './AnimatedHero';
import PaintCanvas from './PaintCanvas';
import {
  generateInterior,
  generateExterior,
  generateStyleTransfer,
  generatePaint,
  generateFloor,
  generateReplace
} from './api';
import { useSubscription, canSave, canGenerate } from './hooks/useSubscription';
import SubscriptionPaywall from './components/SubscriptionPaywall';
import { initRevenueCat, logInUser } from './utils/revenuecat';
import { trackReviewSubmitted } from './utils/analytics';
import './DesignWorkspace.css';
import './Landing.css'; // Import Landing styles for the form

// Generating phrases for animation (same as Landing.js)
const generatingPhrases = [
  'Preparing your room...',
  'Analyzing the space...',
  'Processing room layout...',
  'Scanning room dimensions...',
  'Identifying design elements...',
  'Applying your style...',
  'Generating design...',
  'Almost there...'
];

// Rotating phrase component for loading animation with progress bar
function GeneratingAnimation() {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [fadeClass, setFadeClass] = React.useState('fade-in');
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    // Progress animation - simulate progress from 0 to 95%
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return 95; // Stop at 95% until generation completes
        return prev + Math.random() * 3 + 1; // Increment by 1-4% randomly
      });
    }, 200);

    // Text rotation
    const textInterval = setInterval(() => {
      setFadeClass('fade-out');
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % generatingPhrases.length);
        setFadeClass('fade-in');
      }, 300);
    }, 2500);

    return () => {
      clearInterval(progressInterval);
      clearInterval(textInterval);
    };
  }, []);

  return (
    <div className="generating-animation-container">
      <span className={`generating-text ${fadeClass}`}>
        {generatingPhrases[currentIndex]}
      </span>
      <div className="progress-bar-container">
        <div className="progress-bar" style={{ width: `${progress}%` }}></div>
      </div>
      <span className="progress-percentage">{Math.round(progress)}%</span>
    </div>
  );
}

// Import constants from Landing (we'll need to duplicate them here)
const ACCEPTED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const ACCEPTED_FORMATS_TEXT = 'JPG, PNG, WEBP, GIF';

const ROOM_TYPES = [
  'kitchen', 'living room', 'home office', 'bathroom', 'dining', 
  'bedroom', 'study room', 'gaming room', 'office', 'attic'
];

const STYLES = [
  'custom', 'christmas', 'modern', 'tropical', 'minimalistic', 'bohemian',
  'rustic', 'vintage', 'baroque', 'mediterranean', 'cyberpunk', 'ancient egyptian',
  'soho style', 'luxury', 'gamer', 'cozy', 'technoland', 'coastal', 'japanese',
  'cottagecore', 'gothic', 'ski chalet', 'medieval', '80s style', 'cartoon', 'wood'
];

const STYLE_IMAGES = {
  'custom': 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=150&fit=crop',
  'christmas': 'https://images.unsplash.com/photo-1706510301252-c118fc2eb552?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MjB8fGNocmlzdG1hcyUyMGRlY29yfGVufDB8fDB8fHww',
  'modern': 'https://images.unsplash.com/photo-1593696140826-c58b021acf8b?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bW9kZXJuJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'tropical': 'https://images.unsplash.com/photo-1760067538559-49b2bfb5f42a?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MjR8fHRyb3BpY2FsJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'minimalistic': 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8bWluaW1hbGlzdGljJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'bohemian': 'https://images.unsplash.com/photo-1622763846204-5d0bf5031e06?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fGJvaGVtaWFuJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'rustic': 'https://images.unsplash.com/photo-1729932989809-f473b1523a9b?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTV8fHJ1c3RpYyUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'vintage': 'https://images.unsplash.com/photo-1646242571098-43d06a80466b?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NTh8fHZpbnRhZ2UlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'baroque': 'https://images.unsplash.com/photo-1694514291510-018e4ffdf245?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTZ8fGJhcm9xdWUlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'mediterranean': 'https://images.unsplash.com/photo-1667054430237-2a2afe4cb863?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mzl8fG1lZGl0ZXJyYW5lYW4lMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'cyberpunk': 'https://images.unsplash.com/photo-1649977107006-b769969c9725?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8N3x8Y3liZXJwdW5rJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'ancient egyptian': 'https://images.unsplash.com/photo-1624697156736-e846a4444904?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTl8fGFuY2llbnQlMjBlZ3lwdGlhbiUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'soho style': 'https://images.unsplash.com/photo-1677568554453-ae5baf28da6c?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8c29obyUyMHN0eWxlJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'luxury': 'https://images.unsplash.com/photo-1613545325268-9265e1609167?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8bHV4dXJ5JTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'gamer': 'https://images.unsplash.com/photo-1609815697866-033945af9797?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MzF8fGdhbWVyJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'cozy': 'https://images.unsplash.com/photo-1505693070124-1cce01268b88?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8Y296eSUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'technoland': 'https://images.unsplash.com/photo-1659894748283-74e2ccf7ea22?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mzl8fGN5YmVycHVuayUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'coastal': 'https://plus.unsplash.com/premium_photo-1736194029337-2c866fe5982f?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NzZ8fGNvYXN0YWwlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'japanese': 'https://images.unsplash.com/photo-1735964519674-440909118206?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8amFwYW5lc2UlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'cottagecore': 'https://images.unsplash.com/photo-1604629761628-5640ee399e18?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MzZ8fGNvdHRhZ2Vjb3JlJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'gothic': 'https://images.unsplash.com/photo-1725270865205-a9a5b9d925d4?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NTl8fGdvdGhpYyUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'ski chalet': 'https://images.unsplash.com/photo-1676661647337-1036036e1d5e?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NTV8fHNraSUyMGNoYWxldCUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'medieval': 'https://images.unsplash.com/photo-1739999064283-19ea78ab10e1?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTV8fG1lZGlldmFsJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  '80s style': 'https://images.unsplash.com/photo-1765734482970-da3c114d06c7?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8ODBzJTIwc3R5bGUlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'cartoon': 'https://images.unsplash.com/photo-1612355524127-d361afbfc818?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8YW5pbWF0ZWQlMjBjYXJ0b29uJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'wood': 'https://images.unsplash.com/photo-1704265587121-37452a68ab05?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mzl8fHdvb2QlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww'
};

const COLOR_PALETTES = {
  'random': ['#FF6B9D', '#4ECDC4', '#FFE66D', '#95E1D3', '#F38181'],
  'millennial gray': ['#E8E8E8', '#D3D3D3', '#C0C0C0', '#A9A9A9', '#808080'],
  'terracotta mirage': ['#E07A5F', '#F2CC8F', '#E9C46A', '#D4A373', '#BC6C25'],
  'neon sunset': ['#FF6B9D', '#FF8C42', '#FFB347', '#FF6B35', '#FF8C69'],
  'forest hues': ['#2D5016', '#3D6B2A', '#5A8F3E', '#7FB069', '#95C085'],
  'peach orchard': ['#FFE5B4', '#FFCCCB', '#FFB6C1', '#FFA07A', '#FF8C69'],
  'fuchsia blossom': ['#FF1493', '#FF69B4', '#FFB6C1', '#FFC0CB', '#FFB6C1'],
  'emerald gem': ['#50C878', '#2E8B57', '#3CB371', '#66CDAA', '#7FFFD4'],
  'pastel breeze': ['#FFB6C1', '#B0E0E6', '#FFE4E1', '#E0E6FF', '#FFF0F5'],
  'azure mirage': ['#007FFF', '#1E90FF', '#87CEEB', '#B0E0E6', '#E0F6FF'],
  'twilight blue': ['#191970', '#000080', '#4169E1', '#6495ED', '#87CEEB'],
  'earth harmony': ['#8B4513', '#A0522D', '#CD853F', '#DEB887', '#F4A460'],
  'arctic lavender': ['#E6E6FA', '#DDA0DD', '#DA70D6', '#BA55D3', '#9370DB'],
  'antique sage': ['#9CAF88', '#87A96B', '#6B8E23', '#8FBC8F', '#98D8C8'],
  'earthy hues': ['#8B4513', '#A0522D', '#CD853F', '#D2691E', '#F4A460'],
  'velvet dusk': ['#4B0082', '#6A0DAD', '#8B008B', '#9370DB', '#BA55D3'],
  'ocean mist': ['#20B2AA', '#48D1CC', '#00CED1', '#5F9EA0', '#87CEEB'],
  'army dream': ['#556B2F', '#6B8E23', '#808000', '#9ACD32', '#ADFF2F'],
  'sakura bloom': ['#FFB6C1', '#FFC0CB', '#FFDAB9', '#FFE4E1', '#FFF0F5'],
  'lilac love': ['#DDA0DD', '#DA70D6', '#BA55D3', '#9370DB', '#8B7D9B'],
  'whimsical wish': ['#FF69B4', '#FF1493', '#FFB6C1', '#FFC0CB', '#FFE4E1'],
  'turquoise lagoon': ['#40E0D0', '#00CED1', '#48D1CC', '#5F9EA0', '#7FFFD4']
};

// Building types for exterior design
const BUILDING_TYPES = [
  'apartment',
  'house',
  'office_building',
  'residential',
  'retail',
  'villa'
];

// Building type images - using Unsplash search URLs
const BUILDING_TYPE_IMAGES = {
  'apartment': 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8YXBhcnRtZW50JTIwYnVpbGRpbmd8ZW58MHx8MHx8fDA%3D',
  'house': 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8aG91c2UlMjBleHRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'office_building': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8b2ZmaWNlJTIwYnVpbGRpbmclMjBleHRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'residential': 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8cmVzaWRlbnRpYWwlMjBob3VzZXxlbnwwfHwwfHx8MA%3D%3D',
  'retail': 'https://images.unsplash.com/photo-1556910096-6f5e72db6803?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8cmV0YWlsJTIwc3RvcmUlMjBmYWNhZGV8ZW58MHx8MHx8fDA%3D',
  'villa': 'https://images.unsplash.com/photo-1613977257363-707ba9348227?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8dmlsbGElMjBleHRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D'
};

// Floor materials for floor restyle
const FLOOR_MATERIALS = [
  'brick',
  'wood',
  'cobble',
  'tiles',
  'carpet',
  'concrete',
  'marble',
  'hardwood',
  'laminate',
  'vinyl',
  'custom'  // Note: 'custom' requires customFloorDescription
];

// Feature definitions for thumbnails
const FEATURES = [
  {
    id: 'interior',
    title: 'Interior Design',
    description: 'Transform your interior spaces',
    thumbnail: 'https://iili.io/fMA6Zvt.png'
  },
  {
    id: 'exterior',
    title: 'Exterior Design',
    description: 'Redesign outdoor spaces',
    thumbnail: 'https://iili.io/fMAi4gp.png'
  },
  {
    id: 'style',
    title: 'Reference Style',
    description: 'Show AI what you like and let it style your space',
    thumbnail: 'https://iili.io/fMA6Zvt.png'
  },
  {
    id: 'paint',
    title: 'Paint',
    description: 'Visualize paint colors',
    thumbnail: 'https://iili.io/fMAbQcJ.png'
  },
  {
    id: 'floor',
    title: 'Floor Restyle',
    description: 'Redesign your floors',
    thumbnail: 'https://iili.io/fMROkZB.png'
  },
  {
    id: 'replace',
    title: 'Replace Object',
    description: 'Replace objects in space',
    thumbnail: 'https://iili.io/fMRWlIt.png'
  }
];

function DesignWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const subscription = useSubscription(user);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  
  // Form state (for modal)
  const [roomImage, setRoomImage] = useState(null);
  const [selectedExampleImage, setSelectedExampleImage] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState('');
  const [roomType, setRoomType] = useState('');
  const [style, setStyle] = useState('');
  const [colorPalette, setColorPalette] = useState('');
  const [customStylePrompt, setCustomStylePrompt] = useState('');
  const [exampleImages, setExampleImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showAllStyles, setShowAllStyles] = useState(false);
  const [referenceSizeValue, setReferenceSizeValue] = useState('');
  const [referenceSizeUnit, setReferenceSizeUnit] = useState('in');
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const colorPaletteGridRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  
  // Feature-specific state
  const [buildingType, setBuildingType] = useState('');
  const [floorMaterial, setFloorMaterial] = useState('');
  const [customFloorDescription, setCustomFloorDescription] = useState('');
  const [floorColor, setFloorColor] = useState('');
  const [floorMask, setFloorMask] = useState(null);
  const [paintColor, setPaintColor] = useState('#FF5733');
  const [paintMask, setPaintMask] = useState(null);
  const [objectMask, setObjectMask] = useState(null);
  const [styleReferenceImage, setStyleReferenceImage] = useState(null);
  const [styleReferenceUrl, setStyleReferenceUrl] = useState('');
  const [replacementObjectImage, setReplacementObjectImage] = useState(null);
  const [replacementObjectUrl, setReplacementObjectUrl] = useState('');
  const [replacementObjectDescription, setReplacementObjectDescription] = useState('');
  const [showPaintCanvas, setShowPaintCanvas] = useState(false);
  const [paintCanvasMode, setPaintCanvasMode] = useState('floor');
  const [paintCanvasImage, setPaintCanvasImage] = useState(null);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  // Load saved images from Firestore
  const loadSavedImages = async (userId) => {
    try {
      const imagesRef = collection(db, 'generatedImages');
      const q = query(
        imagesRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const images = [];
      querySnapshot.forEach((doc) => {
        images.push(doc.data().imageUrl);
      });
      setGeneratedImages(images);
    } catch (error) {
      console.error('Error loading images:', error);
    }
  };

  // Check authentication and load saved images
  useEffect(() => {
    // Initialize RevenueCat SDK
    initRevenueCat();
    
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/');
      } else {
        setUser(currentUser);
        // Load user's saved images from Firestore
        await loadSavedImages(currentUser.uid);
        // Log in user to RevenueCat (link Firebase UID)
        await logInUser(currentUser.uid);
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Save image to Firebase Storage and Firestore
  const saveImageToFirebase = async (imageDataUrl, userId, designId) => {
    try {
      // Check storage limit before saving
      if (!canSave(subscription)) {
        setShowSubscriptionModal(true);
        throw new Error('Storage limit reached. Upgrade to Pro for unlimited storage.');
      }
      
      // Convert base64 to blob and upload to Storage
      const imageRef = ref(storage, `generated-images/${userId}/${Date.now()}.png`);
      await uploadString(imageRef, imageDataUrl, 'data_url');

      // Get download URL
      const downloadURL = await getDownloadURL(imageRef);

      // Save metadata to Firestore
      const docRef = await addDoc(collection(db, 'generatedImages'), {
        userId: userId,
        designId: designId,
        imageUrl: downloadURL,
        createdAt: new Date(),
      });

      // Refresh subscription status to update design count
      if (subscription?.refresh) {
        subscription.refresh();
      }

      return downloadURL;
    } catch (error) {
      console.error('Error saving image to Firebase:', error);
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // File validation
  const validateFile = (file) => {
    if (!file) return { valid: false, error: 'No file selected' };
    if (!ACCEPTED_FORMATS.includes(file.type)) {
      return { valid: false, error: `Invalid file format. Accepted formats: ${ACCEPTED_FORMATS_TEXT}` };
    }
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return { valid: false, error: `File too large. Maximum size: 10MB` };
    }
    return { valid: true, error: null };
  };

  const handleRoomImageSelect = (file) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }
    setError(null);
    setRoomImage(file);
    setSelectedExampleImage(null);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleRoomImageSelect(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleRoomImageSelect(file);
  };

  // Camera handlers
  const handleCameraClick = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setStream(mediaStream);
      setShowCamera(true);
    } catch (err) {
      setError('Unable to access camera. Please check permissions or use file upload instead.');
    }
  };

  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(err => console.error('Error playing video:', err));
    }
  }, [showCamera, stream]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
          handleRoomImageSelect(file);
          closeCamera();
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  // Determine if submit button should be disabled based on selected feature
  const getSubmitDisabled = () => {
    // Basic validation: must have room image or example image
    if (!roomImage && !selectedExampleImage) {
      return true;
    }

    // Feature-specific validation
    if (selectedFeature === 'interior') {
      return !style || !colorPalette;
    }
    if (selectedFeature === 'exterior') {
      return !buildingType || !style || !colorPalette;
    }
    if (selectedFeature === 'style') {
      return !styleReferenceImage && !styleReferenceUrl;
    }
    if (selectedFeature === 'paint') {
      return !paintMask || !paintColor;
    }
    if (selectedFeature === 'floor') {
      const missingMask = !floorMask;
      const missingMaterial = !floorMaterial;
      const missingCustomDesc = floorMaterial === 'custom' && !customFloorDescription;
      return missingMask || missingMaterial || missingCustomDesc;
    }
    if (selectedFeature === 'replace') {
      return !objectMask || (!replacementObjectImage && !replacementObjectUrl && !replacementObjectDescription);
    }

    // Old product placement feature
    if (!selectedFeature) {
      return !productImageUrl.trim();
    }

    return false;
  };

  // Submit handler - handles all features
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError(null);

    // Check generation limit - use subscription status
    const canGenerateNow = canGenerate(subscription);
    if (!canGenerateNow) {
      setShowSubscriptionModal(true);
      return;
    }

    // Basic validation
    if (!roomImage && !selectedExampleImage) {
      setError('Please upload a room image or select an example image');
      return;
    }

    // Feature-specific validation
    if (selectedFeature === 'interior') {
      if (!style) {
        setError('Please select a style');
        return;
      }
      if (!colorPalette) {
        setError('Please select a color palette');
        return;
      }
    }
    if (selectedFeature === 'exterior') {
      if (!buildingType) {
        setError('Please select a building type');
        return;
      }
      if (!style) {
        setError('Please select a style');
        return;
      }
      if (!colorPalette) {
        setError('Please select a color palette');
        return;
      }
    }
    if (selectedFeature === 'style') {
      if (!styleReferenceImage && !styleReferenceUrl) {
        setError('Please upload or provide URL of style reference image');
        return;
      }
    }
    if (selectedFeature === 'paint') {
      if (!paintMask) {
        setError('Please upload a paint mask');
        return;
      }
      if (!paintColor) {
        setError('Please select a paint color');
        return;
      }
    }
    if (selectedFeature === 'floor') {
      if (!floorMask) {
        setError('Please upload a floor mask image');
        return;
      }
      if (!floorMaterial) {
        setError('Please select a floor material');
        return;
      }
      if (floorMaterial === 'custom' && !customFloorDescription) {
        setError('Please provide a custom floor description');
        return;
      }
    }
    if (selectedFeature === 'replace') {
      if (!objectMask) {
        setError('Please upload an object mask');
        return;
      }
      if (!replacementObjectImage && !replacementObjectUrl && !replacementObjectDescription) {
        setError('Please provide replacement object (image, URL, or description)');
        return;
      }
    }

    // Old product placement validation
    if (!selectedFeature) {
      if (!productImageUrl.trim()) {
        setError('Please enter a product image URL');
        return;
      }
      try {
        new URL(productImageUrl.trim());
      } catch {
        setError('Please enter a valid product image URL');
        return;
      }
    }

    if (style === 'custom' && !customStylePrompt.trim()) {
      setError('Please enter a custom style description');
      return;
    }

    setLoading(true);

    try {
      let data;

      if (selectedFeature) {
        // Use feature-specific API calls
        const params = {
          roomImage: roomImage,
          exampleImageUrl: selectedExampleImage,
          roomType: roomType,
          style: style,
          colorPalette: colorPalette,
        };

        if (style === 'custom' && customStylePrompt) {
          params.customStylePrompt = customStylePrompt.trim();
        }

        // Feature-specific params
        switch (selectedFeature) {
          case 'interior':
            data = await generateInterior(params, user);
            break;
          case 'exterior':
            params.buildingType = buildingType;
            data = await generateExterior(params, user);
            break;
          case 'style':
            params.styleReferenceImage = styleReferenceImage;
            params.styleReferenceUrl = styleReferenceUrl;
            data = await generateStyleTransfer(params, user);
            break;
          case 'paint':
            params.paintMask = paintMask;
            params.paintColor = paintColor;
            data = await generatePaint(params, user);
            break;
          case 'floor':
            params.floorMask = floorMask;
            params.floorMaterial = floorMaterial;
            params.customFloorDescription = customFloorDescription;
            params.floorColor = floorColor;
            data = await generateFloor(params, user);
            break;
          case 'replace':
            params.objectMask = objectMask;
            params.replacementObjectImage = replacementObjectImage;
            params.replacementObjectUrl = replacementObjectUrl;
            params.replacementObjectDescription = replacementObjectDescription;
            data = await generateReplace(params, user);
            break;
          default:
            throw new Error(`Unknown feature: ${selectedFeature}`);
        }
      } else {
        // Original endpoint for backward compatibility (product placement)
        const formData = new FormData();
        
        if (roomImage) {
          formData.append('room_image', roomImage);
        } else if (selectedExampleImage) {
          formData.append('example_image_url', selectedExampleImage);
        }
        
        formData.append('product_image_url', productImageUrl.trim());
        formData.append('product_type', 'product');
        
        if (roomType) formData.append('room_type', roomType);
        if (style) formData.append('style', style);
        if (colorPalette) formData.append('color_palette', colorPalette);
        if (style === 'custom' && customStylePrompt) {
          formData.append('custom_style_prompt', customStylePrompt.trim());
        }

        if (referenceSizeValue && !Number.isNaN(Number(referenceSizeValue))) {
          formData.append('reference_size_value', String(referenceSizeValue));
          formData.append('reference_size_unit', referenceSizeUnit);
        }

        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${apiUrl}/generate-room`, {
          method: 'POST',
          body: formData,
        });

        data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Something went wrong');
        }
      }

      if (data.image_b64) {
        const imageData = `data:image/png;base64,${data.image_b64}`;
        
        // Save to Firebase if user is authenticated and has storage available
        if (user && canSave(subscription)) {
          try {
            const downloadURL = await saveImageToFirebase(imageData, user.uid, id);
            setGeneratedImages(prev => [...prev, downloadURL]);
          } catch (error) {
            console.error('Failed to save to Firebase, using local storage:', error);
            setGeneratedImages(prev => [...prev, imageData]);
          }
        } else {
          setGeneratedImages(prev => [...prev, imageData]);
        }
        
        // Refresh subscription status to update generationsRemaining
        if (subscription?.refresh) {
          subscription.refresh();
        }
        
        // Close modal and reset form
        setShowGenerateModal(false);
        resetForm();
      } else {
        setError('Failed to generate image');
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRoomImage(null);
    setSelectedExampleImage(null);
    setProductImageUrl('');
    setRoomType('');
    setStyle('');
    setColorPalette('');
    setCustomStylePrompt('');
    setImagePreview(null);
    setError(null);
    setReferenceSizeValue('');
    setReferenceSizeUnit('in');
    // Reset feature-specific state
    setBuildingType('');
    setFloorMaterial('');
    setCustomFloorDescription('');
    setFloorColor('');
    setFloorMask(null);
    setPaintColor('#FF5733');
    setPaintMask(null);
    setObjectMask(null);
    setStyleReferenceImage(null);
    setStyleReferenceUrl('');
    setReplacementObjectImage(null);
    setReplacementObjectUrl('');
    setReplacementObjectDescription('');
    setSelectedFeature(null);
  };

  const handleExampleImageSelect = (url) => {
    setSelectedExampleImage(url);
    setRoomImage(null);
    setImagePreview(null);
  };

  // Handle review submission
  const handleReviewSubmit = async () => {
    if (!reviewText.trim()) {
      return; // Don't submit empty reviews
    }

    if (!user) {
      alert('Please sign in to submit a review');
      return;
    }

    try {
      // Save review to Firestore
      await addDoc(collection(db, 'reviews'), {
        userId: user.uid,
        userEmail: user.email || '',
        userName: user.displayName || '',
        reviewText: reviewText.trim(),
        createdAt: new Date(),
        designId: id || null,
      });

      // Track review submission
      trackReviewSubmitted(reviewText.trim().length);

      // Show confirmation
      setReviewSubmitted(true);
      setReviewText(''); // Clear input

      // Hide confirmation message after 5 seconds and return to input state
      setTimeout(() => {
        setReviewSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    }
  };

  if (authLoading) {
    return (
      <div className="workspace-loading">
        <div className="loading-spinner"></div>
        <p>Loading workspace...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="design-workspace">
      {/* Header */}
      <header className="workspace-header">
        <div className="workspace-header-left">
          <div 
            className="workspace-logo-container"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <img 
              src="https://iili.io/f0u0nyP.png" 
              alt="AI Home Designer Logo" 
              className="workspace-logo-img"
            />
            <h1 className="workspace-logo">AI Home Designer</h1>
          </div>
          <span className="workspace-id">Design: {id}</span>
        </div>
        <div className="workspace-header-right">
          {!subscription?.isPro && (
            <button 
              className="get-pro-btn"
              onClick={() => setShowSubscriptionModal(true)}
            >
              Premium
            </button>
          )}
          <div className="user-info-compact">
            {user.photoURL ? (
              <img src={user.photoURL} alt={user.displayName || 'User'} className="user-avatar-small" />
            ) : (
              <div className="user-avatar-small-placeholder">
                {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
              </div>
            )}
            <span className="user-name-compact">{user.displayName || user.email}</span>
          </div>
          <button className="header-btn" onClick={() => navigate('/')} title="Home">
            <FiHome />
          </button>
          <button className="header-btn" onClick={handleSignOut} title="Sign Out">
            <FiLogOut />
          </button>
        </div>
      </header>

      {/* Main Content - 2 Horizontal Boxes */}
      <div className="workspace-content-boxes">
        {/* Box 1: App Coming Soon with Review */}
        <div className="workspace-box">
          <div className="box-content">
            <h2 className="box-title">AI Home Designer App Coming Soon</h2>
            <p className="box-description">Get the app for an even better experience</p>
            <div className="review-section">
              <p className="review-prompt">In the meantime, leave us a review:</p>
              {reviewSubmitted ? (
                <div className="review-confirmation">
                  <p className="review-confirmation-message">
                    ✓ Message has been sent! We will promptly respond in 20-30 minutes.
                  </p>
                </div>
              ) : (
                <>
                  <textarea
                    className="review-input"
                    placeholder="Share your thoughts..."
                    rows="4"
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                  />
                  <button 
                    className="submit-review-btn"
                    onClick={handleReviewSubmit}
                    disabled={!reviewText.trim()}
                  >
                    Submit Review
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Box 2: My Collections */}
        <div className="workspace-box">
          <div className="box-content">
            <h2 className="box-title">My Collections</h2>
            {generatedImages.length === 0 ? (
              <div className="empty-collection">
                <p className="empty-text">No images generated yet</p>
                <div className="features-thumbnails-grid">
                  {FEATURES.map(feature => (
                    <div key={feature.id} className="feature-thumbnail-card">
                      <img 
                        src={feature.thumbnail} 
                        alt={feature.title}
                        className="feature-thumbnail-image"
                      />
                      <div className="feature-thumbnail-content">
                        <h3 className="feature-thumbnail-title">{feature.title}</h3>
                        <p className="feature-thumbnail-description">{feature.description}</p>
                        <button
                          className="feature-thumbnail-cta"
                          onClick={() => {
                            setSelectedFeature(feature.id);
                            setShowGenerateModal(true);
                          }}
                        >
                          Try it
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="collection-grid">
                {generatedImages.map((img, idx) => (
                  <div key={idx} className="collection-item">
                    <img src={img} alt={`Generated design ${idx + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Animated Hero Section */}
      <AnimatedHero onGenerateClick={() => setShowGenerateModal(true)} />

      {/* Footer */}
      <footer className="workspace-footer">
        <div className="footer-content">
          <p>&copy; 2025 AI Home Designer. All rights reserved.</p>
          <div className="footer-links">
            <a href="mailto:stainstartups@gmail.com">Contact</a>
          </div>
        </div>
      </footer>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="generate-modal-overlay" onClick={() => !loading && setShowGenerateModal(false)}>
          <div className="generate-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Generate New Design</h2>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowGenerateModal(false)}
                disabled={loading}
              >
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="generator-form">
              {/* Feature Title */}
              {selectedFeature && (
                <div className="form-group">
                  <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>
                    {selectedFeature === 'interior' && 'Interior Design'}
                    {selectedFeature === 'exterior' && 'Exterior Design'}
                    {selectedFeature === 'style' && 'Style Transfer'}
                    {selectedFeature === 'paint' && 'Paint Room'}
                    {selectedFeature === 'floor' && 'Floor Restyle'}
                    {selectedFeature === 'replace' && 'Replace Object'}
                  </h2>
                </div>
              )}

              {/* Step 1: Building Type (Exterior only) OR Room Type (Interior/others) */}
              {selectedFeature === 'exterior' ? (
                <div className="form-group form-step">
                  <div className="step-indicator">
                    <span className="step-number">1</span>
                    <label className="form-label">Building Type (required)</label>
                  </div>
                  <div className="style-grid-container building-type-grid">
                    {BUILDING_TYPES.map(type => (
                      <div
                        key={type}
                        className={`style-card ${buildingType === type ? 'selected' : ''}`}
                        onClick={() => setBuildingType(buildingType === type ? '' : type)}
                      >
                        <div className="style-card-title">
                          {type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </div>
                        <div className="style-card-image">
                          <img 
                            src={BUILDING_TYPE_IMAGES[type] || BUILDING_TYPE_IMAGES['house']} 
                            alt={type}
                            onError={(e) => {
                              e.target.src = BUILDING_TYPE_IMAGES['house'];
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : !selectedFeature || selectedFeature === 'interior' || selectedFeature === 'style' || selectedFeature === 'paint' || selectedFeature === 'floor' || selectedFeature === 'replace' ? (
                <div className="form-group form-step">
                  <div className="step-indicator">
                    <span className="step-number">1</span>
                    <label className="form-label">Room Type (optional)</label>
                  </div>
                  <select
                    value={roomType}
                    onChange={(e) => setRoomType(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select room type (optional)</option>
                    {ROOM_TYPES.map(type => (
                      <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                    ))}
                  </select>
                </div>
              ) : null}

              {/* Step 2: Room/Exterior Image Upload OR Example Image Selection */}
              <div className="form-group form-step">
                <div className="step-indicator">
                  <span className="step-number">2</span>
                  <label className="form-label">
                    {selectedFeature === 'exterior' ? 'Exterior Image' : 'Room Image'}
                  </label>
                </div>
                <div 
                  ref={dropZoneRef}
                  className={`file-upload-zone ${isDragging ? 'dragging' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <label htmlFor="modal-room-image" className="file-label">
                    {imagePreview ? (
                      <div className="image-preview-box">
                        <img src={imagePreview} alt="Room preview" className="preview-img" />
                        <span className="change-text">Change</span>
                      </div>
                    ) : selectedExampleImage ? (
                      <div className="image-preview-box">
                        <img src={selectedExampleImage} alt="Example room" className="preview-img" />
                        <span className="change-text">Change</span>
                      </div>
                    ) : (
                      <div className="file-upload-box">
                        <FiCamera className="upload-icon" />
                        <span>Room Photo</span>
                        <span className="format-hint">Accepted: {ACCEPTED_FORMATS_TEXT}</span>
                        <span className="drag-hint">Drag & drop or click to upload</span>
                      </div>
                    )}
                  </label>
                  <input
                    ref={fileInputRef}
                    id="modal-room-image"
                    type="file"
                    accept={ACCEPTED_FORMATS.join(',')}
                    onChange={handleFileChange}
                    className="file-input"
                  />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  {!imagePreview && !selectedExampleImage && (
                    <div className="upload-options">
                      <button
                        type="button"
                        onClick={handleCameraClick}
                        className="upload-option-btn camera-btn"
                      >
                        <FiCamera className="camera-icon-white" />
                        Take Photo
                      </button>
                    </div>
                  )}
                </div>

                {/* Example Images Selection - Hide for exterior feature */}
                {selectedFeature !== 'exterior' && roomType && exampleImages[roomType] && !imagePreview && (
                  <div className="example-images-section">
                    <p className="example-images-label">Or choose from examples:</p>
                    <div className="example-images-grid">
                      {exampleImages[roomType].slice(0, 6).map((url, idx) => (
                        <div
                          key={idx}
                          className={`example-image-card ${selectedExampleImage === url ? 'selected' : ''}`}
                          onClick={() => handleExampleImageSelect(url)}
                        >
                          <img src={url} alt={`Example ${idx + 1}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Step 3: Feature-Specific Fields */}
              {selectedFeature === 'style' && (
                <div className="form-group form-step">
                  <div className="step-indicator">
                    <span className="step-number">3</span>
                    <label className="form-label">Style Reference Image (required)</label>
                  </div>
                  <span className="format-hint">Upload or provide URL of the style image you want to apply</span>
                  <input
                    type="file"
                    accept={ACCEPTED_FORMATS.join(',')}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) setStyleReferenceImage(file);
                    }}
                    className="file-input"
                    style={{ marginTop: '10px' }}
                  />
                  <span style={{ margin: '10px 0', display: 'block', textAlign: 'center' }}>OR</span>
                  <input
                    type="text"
                    placeholder="https://example.com/style-reference.jpg"
                    value={styleReferenceUrl}
                    onChange={(e) => setStyleReferenceUrl(e.target.value)}
                    className="text-input"
                  />
                </div>
              )}

              {selectedFeature === 'paint' && (
                <div className="form-group form-step">
                  <div className="step-indicator">
                    <span className="step-number">3</span>
                    <label className="form-label">Paint Selection (required)</label>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const imgSrc = imagePreview || selectedExampleImage || (roomImage ? URL.createObjectURL(roomImage) : null);
                        if (imgSrc) {
                          setPaintCanvasImage(imgSrc);
                          setPaintCanvasMode('paint');
                          setShowPaintCanvas(true);
                        } else {
                          setError('Please upload a room image first');
                        }
                      }}
                      className="upload-option-btn camera-btn"
                      style={{ width: '100%', marginBottom: '10px' }}
                    >
                      🎨 Paint Areas to Change
                    </button>
                    <p style={{ margin: '8px 0', fontSize: '0.9rem', color: '#666', textAlign: 'center' }}>OR</p>
                    <label className="form-label" style={{ display: 'block', marginTop: '10px' }}>
                      Upload Paint Mask Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) setPaintMask(file);
                      }}
                      className="file-input"
                      style={{ marginTop: '10px' }}
                    />
                    {paintMask && (
                      <p style={{ marginTop: '8px', color: '#30a6b1', fontSize: '0.9rem' }}>
                        ✓ Mask uploaded: {paintMask.name}
                      </p>
                    )}
                  </div>
                  <div style={{ marginTop: '15px' }}>
                    <label className="form-label">Paint Color (hex code)</label>
                    <input
                      type="color"
                      value={paintColor}
                      onChange={(e) => setPaintColor(e.target.value)}
                      style={{ width: '100%', height: '50px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={paintColor}
                      onChange={(e) => setPaintColor(e.target.value)}
                      placeholder="#FF5733"
                      className="text-input"
                      style={{ marginTop: '10px' }}
                    />
                  </div>
                </div>
              )}

              {selectedFeature === 'floor' && (
                <div className="form-group form-step">
                  <div className="step-indicator">
                    <span className="step-number">3</span>
                    <label className="form-label">Floor Selection (required)</label>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '10px' }}>
                      Floor Selection (required)
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const imgSrc = imagePreview || selectedExampleImage || (roomImage ? URL.createObjectURL(roomImage) : null);
                        if (imgSrc) {
                          setPaintCanvasImage(imgSrc);
                          setPaintCanvasMode('floor');
                          setShowPaintCanvas(true);
                        } else {
                          setError('Please upload a room image first');
                        }
                      }}
                      className="upload-option-btn camera-btn"
                      style={{ width: '100%', marginBottom: '10px' }}
                    >
                      🎨 Paint Floor Areas
                    </button>
                    <p style={{ margin: '8px 0', fontSize: '0.9rem', color: '#666', textAlign: 'center' }}>OR</p>
                    <label className="form-label" style={{ display: 'block', marginTop: '10px' }}>
                      Upload Floor Mask Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) setFloorMask(file);
                      }}
                      className="file-input"
                      style={{ marginTop: '10px' }}
                    />
                    {floorMask && (
                      <p style={{ marginTop: '8px', color: '#30a6b1', fontSize: '0.9rem' }}>
                        ✓ Mask uploaded: {floorMask.name}
                      </p>
                    )}
                  </div>
                  <div style={{ marginTop: '15px' }}>
                    <label className="form-label">Floor Material (required)</label>
                    <select
                      value={floorMaterial}
                      onChange={(e) => setFloorMaterial(e.target.value)}
                      className="form-select"
                      required
                    >
                      <option value="">Select floor material</option>
                      {FLOOR_MATERIALS.map(material => (
                        <option key={material} value={material}>
                          {material.charAt(0).toUpperCase() + material.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {floorMaterial === 'custom' && (
                    <div style={{ marginTop: '15px' }}>
                      <label className="form-label">Custom Floor Description (required)</label>
                      <textarea
                        value={customFloorDescription}
                        onChange={(e) => setCustomFloorDescription(e.target.value)}
                        placeholder="Describe your custom floor material..."
                        className="form-textarea"
                        rows="3"
                        required
                      />
                    </div>
                  )}
                  <div style={{ marginTop: '15px' }}>
                    <label className="form-label">Floor Color (optional, hex code)</label>
                    <input
                      type="color"
                      value={floorColor || '#FFFFFF'}
                      onChange={(e) => setFloorColor(e.target.value)}
                      style={{ width: '100%', height: '50px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={floorColor}
                      onChange={(e) => setFloorColor(e.target.value)}
                      placeholder="#FFFFFF (optional)"
                      className="text-input"
                      style={{ marginTop: '10px' }}
                    />
                  </div>
                </div>
              )}

              {selectedFeature === 'replace' && (
                <div className="form-group form-step">
                  <div className="step-indicator">
                    <span className="step-number">3</span>
                    <label className="form-label">Object Selection (required)</label>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const imgSrc = imagePreview || selectedExampleImage || (roomImage ? URL.createObjectURL(roomImage) : null);
                        if (imgSrc) {
                          setPaintCanvasImage(imgSrc);
                          setPaintCanvasMode('replace');
                          setShowPaintCanvas(true);
                        } else {
                          setError('Please upload a room image first');
                        }
                      }}
                      className="upload-option-btn camera-btn"
                      style={{ width: '100%', marginBottom: '10px' }}
                    >
                      🎨 Paint Object to Replace
                    </button>
                    <p style={{ margin: '8px 0', fontSize: '0.9rem', color: '#666', textAlign: 'center' }}>OR</p>
                    <label className="form-label" style={{ display: 'block', marginTop: '10px' }}>
                      Upload Object Mask Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) setObjectMask(file);
                      }}
                      className="file-input"
                      style={{ marginTop: '10px' }}
                    />
                    {objectMask && (
                      <p style={{ marginTop: '8px', color: '#30a6b1', fontSize: '0.9rem' }}>
                        ✓ Mask uploaded: {objectMask.name}
                      </p>
                    )}
                  </div>
                  <div style={{ marginTop: '15px' }}>
                    <label className="form-label">Replacement Object</label>
                    <span className="format-hint">Upload image, provide URL, or describe the replacement object</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) setReplacementObjectImage(file);
                      }}
                      className="file-input"
                      style={{ marginTop: '10px' }}
                    />
                    <span style={{ margin: '10px 0', display: 'block', textAlign: 'center' }}>OR</span>
                    <input
                      type="text"
                      placeholder="https://example.com/replacement-object.jpg"
                      value={replacementObjectUrl}
                      onChange={(e) => setReplacementObjectUrl(e.target.value)}
                      className="text-input"
                    />
                    <span style={{ margin: '10px 0', display: 'block', textAlign: 'center' }}>OR</span>
                    <textarea
                      value={replacementObjectDescription}
                      onChange={(e) => setReplacementObjectDescription(e.target.value)}
                      placeholder="Describe the replacement object (e.g., 'modern white coffee table')"
                      className="form-textarea"
                      rows="3"
                    />
                  </div>
                </div>
              )}

              {/* Product Image URL - only show if no feature selected (old product placement) */}
              {!selectedFeature && (
                <div className="form-group form-step">
                  <div className="step-indicator">
                    <span className="step-number">3</span>
                    <label className="form-label">Product Image URL</label>
                  </div>
                  <span className="format-hint">Please provide the URL of the product image you want to see in your space.</span>
                  <input
                    type="text"
                    style={{ marginTop: '10px' }}
                    placeholder="https://example.com/product-image.jpg"
                    value={productImageUrl}
                    onChange={(e) => setProductImageUrl(e.target.value)}
                    className="text-input"
                  />
                </div>
              )}

              {/* Step 4: Style Selector (for interior, exterior, style transfer) */}
              {(selectedFeature === 'interior' || selectedFeature === 'exterior' || selectedFeature === 'style' || !selectedFeature) && (
                <div className="form-group form-step">
                <div className="step-indicator">
                  <span className="step-number">4</span>
                  <label className="form-label">Style (optional)</label>
                </div>
                <div className={`style-grid-container ${showAllStyles ? 'expanded' : ''}`}>
                  {(showAllStyles ? STYLES : STYLES.slice(0, 8)).map(s => (
                    <div
                      key={s}
                      className={`style-card ${style === s ? 'selected' : ''}`}
                      onClick={() => {
                        setStyle(s === style ? '' : s);
                        if (s !== 'custom') {
                          setCustomStylePrompt('');
                        }
                      }}
                    >
                      <div className="style-card-title">{s.charAt(0).toUpperCase() + s.slice(1)}</div>
                      <div className="style-card-image">
                        <img 
                          src={STYLE_IMAGES[s] || STYLE_IMAGES['modern']} 
                          alt={s}
                          onError={(e) => {
                            e.target.src = STYLE_IMAGES['modern'];
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {STYLES.length > 8 && (
                  <button
                    type="button"
                    className="show-more-styles-btn"
                    onClick={() => setShowAllStyles(!showAllStyles)}
                  >
                    {showAllStyles ? 'Show less' : 'Show more'}
                  </button>
                )}

                {/* Custom Style Prompt */}
                {style === 'custom' && (
                  <div className="form-group">
                    <label className="form-label">Custom Style Description</label>
                    <textarea
                      value={customStylePrompt}
                      onChange={(e) => setCustomStylePrompt(e.target.value)}
                      placeholder="Describe your custom style..."
                      className="form-textarea"
                      rows="3"
                    />
                  </div>
                )}
              </div>
              )}

              {/* Step 5: Color Palette Selector (for interior, exterior, style transfer) */}
              {(selectedFeature === 'interior' || selectedFeature === 'exterior' || selectedFeature === 'style' || !selectedFeature) && (
                <div className="form-group form-step">
                  <div className="step-indicator">
                    <span className="step-number">{selectedFeature ? (selectedFeature === 'style' ? '5' : '4') : '5'}</span>
                    <label className="form-label">
                      Color Palette {selectedFeature === 'interior' || selectedFeature === 'exterior' ? '(required)' : '(optional)'}
                    </label>
                  </div>
                <div className="color-palette-container">
                  <button
                    type="button"
                    className="palette-scroll-btn palette-scroll-left"
                    onClick={() => {
                      if (colorPaletteGridRef.current) {
                        colorPaletteGridRef.current.scrollBy({ left: -150, behavior: 'smooth' });
                      }
                    }}
                  >
                    ‹
                  </button>
                  <div className="color-palette-grid" ref={colorPaletteGridRef}>
                    {Object.keys(COLOR_PALETTES).map(palette => (
                      <div
                        key={palette}
                        className={`color-palette-card ${colorPalette === palette ? 'selected' : ''}`}
                        onClick={() => setColorPalette(colorPalette === palette ? '' : palette)}
                      >
                        <div className="color-palette-visual">
                          {palette === 'random' ? (
                            <div className="random-palette-gradient">
                              <svg width="100%" height="100%" viewBox="0 0 100 100">
                                <path
                                  d="M50 20 L50 40 M40 25 L40 35 M60 25 L60 35 M35 30 L65 30 L65 50 L35 50 Z M40 50 L40 60 M60 50 L60 60 M40 60 L60 60 L60 70 L40 70 Z"
                                  stroke="white"
                                  strokeWidth="2"
                                  fill="none"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </div>
                          ) : (
                            <div className="color-bars">
                              {COLOR_PALETTES[palette].map((color, idx) => (
                                <div key={idx} className="color-bar" style={{ backgroundColor: color }} />
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="color-palette-name">
                          {palette === 'random' ? 'Surprise Me' : palette.charAt(0).toUpperCase() + palette.slice(1)}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="palette-scroll-btn palette-scroll-right"
                    onClick={() => {
                      if (colorPaletteGridRef.current) {
                        colorPaletteGridRef.current.scrollBy({ left: 150, behavior: 'smooth' });
                      }
                    }}
                  >
                    ›
                  </button>
                </div>
                </div>
              )}

              {/* Step 6: Reference Size (Optional) - only for old product placement */}
              {!selectedFeature && (
              <div className="form-group form-step reference-size-group">
                <div className="step-indicator">
                  <span className="step-number">6</span>
                  <label className="form-label">Reference size (optional)</label>
                </div>
                <div className="reference-size-row">
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={referenceSizeValue}
                    onChange={(e) => setReferenceSizeValue(e.target.value)}
                    className="reference-size-input"
                    placeholder="e.g. 84"
                  />
                  <div className="reference-size-units">
                    <button
                      type="button"
                      className={`unit-toggle ${referenceSizeUnit === 'in' ? 'active' : ''}`}
                      onClick={() => setReferenceSizeUnit('in')}
                    >
                      in
                    </button>
                    <button
                      type="button"
                      className={`unit-toggle ${referenceSizeUnit === 'cm' ? 'active' : ''}`}
                      onClick={() => setReferenceSizeUnit('cm')}
                    >
                      cm
                    </button>
                  </div>
                </div>
                <p className="reference-size-hint">
                  Width of your main sofa or the wall behind it. Helps estimate furniture sizes.
                </p>
              </div>
              )}

              {error && <div className="error-message">{error}</div>}

              <button
                type="submit"
                disabled={loading || getSubmitDisabled()}
                className="generate-button"
              >
                {loading ? 'Generating...' : <>⭐ Generate ✨</>}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Loading Modal */}
      {loading && (
        <div className="loading-modal-overlay">
          <div className="loading-modal-content">
            <div className="loading-modal-spinner-container">
              <div className="loading-modal-spinner"></div>
            </div>
            <div className="loading-modal-text">
              <GeneratingAnimation />
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <SubscriptionPaywall 
          onClose={() => setShowSubscriptionModal(false)}
          onSuccess={(result) => {
            // Refresh subscription status after successful purchase
            if (subscription?.refresh) {
              subscription.refresh();
            }
            setShowSubscriptionModal(false);
            alert('Welcome to Pro! Your subscription is now active.');
          }}
          user={user}
        />
      )}

      {/* Camera Modal */}
      {showCamera && (
        <div className="camera-modal">
          <div className="camera-modal-content">
            <div className="camera-header">
              <h3>Take Photo</h3>
              <button onClick={closeCamera} className="camera-close-btn">
                <FiX />
              </button>
            </div>
            <video ref={videoRef} autoPlay playsInline className="camera-video" />
            <div className="camera-controls">
              <button onClick={closeCamera} className="camera-btn cancel">Cancel</button>
              <button onClick={capturePhoto} className="camera-btn capture">Capture</button>
            </div>
          </div>
        </div>
      )}

      {/* Paint Canvas Modal */}
      {showPaintCanvas && paintCanvasImage && (
        <PaintCanvas
          imageSrc={paintCanvasImage}
          mode={paintCanvasMode}
          onMaskGenerated={(blob) => {
            // Convert blob to File
            const file = new File([blob], `${paintCanvasMode}_mask.png`, { type: 'image/png' });
            if (paintCanvasMode === 'floor') {
              setFloorMask(file);
            } else if (paintCanvasMode === 'paint') {
              setPaintMask(file);
            } else if (paintCanvasMode === 'replace') {
              setObjectMask(file);
            }
            setShowPaintCanvas(false);
            setPaintCanvasImage(null);
          }}
          onClose={() => {
            setShowPaintCanvas(false);
            setPaintCanvasImage(null);
          }}
        />
      )}
    </div>
  );
}

export default DesignWorkspace;
